import { useEffect, useState } from "react";
import { Mail, Phone, Building2, Trash2 } from "lucide-react";

const fmt = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const STATUSES = [
  { id: "new", label: "New", on: "bg-sky-600 text-white border-sky-600" },
  { id: "contacted", label: "Contacted", on: "bg-amber-500 text-white border-amber-500" },
  { id: "closed", label: "Closed", on: "bg-slate-700 text-white border-slate-700" },
];

const StatusPicker = ({ e, onStatus }) => (
  <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" role="group" data-testid={`enquiry-status-${e.id}`}>
    {STATUSES.map((s) => {
      const active = (e.status || "new") === s.id;
      return (
        <button
          key={s.id}
          onClick={() => !active && onStatus(e.id, s.id)}
          aria-pressed={active}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? s.on : "border-transparent text-slate-600 hover:text-ink"}`}
          data-testid={`enquiry-status-${e.id}-${s.id}`}
        >
          {s.label}
        </button>
      );
    })}
  </div>
);

export const EnquiryCard = ({ e, onDelete, onStatus }) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!confirm) return undefined;
    const t = setTimeout(() => setConfirm(false), 3000);
    return () => clearTimeout(t);
  }, [confirm]);

  return (
    <article className="rounded-3xl border border-sky-100 bg-white p-5 transition-colors hover:border-sky-300 sm:p-6" data-testid={`enquiry-card-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-semibold text-ink">{e.name}</h3>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
            <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 break-all hover:text-sky-700" data-testid={`enquiry-email-${e.id}`}><Mail className="h-4 w-4" />{e.email}</a>
            {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 hover:text-sky-700"><Phone className="h-4 w-4" />{e.phone}</a>}
            {e.organisation && <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" />{e.organisation}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-slate-500" data-testid={`enquiry-date-${e.id}`}>{fmt(e.created_at)}</span>
          <button
            onClick={() => (confirm ? onDelete(e.id) : setConfirm(true))}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs transition-colors ${confirm ? "bg-red-600 text-white" : "text-slate-500 hover:bg-red-50 hover:text-red-600"}`}
            data-testid={`enquiry-delete-${e.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />{confirm ? "Confirm" : ""}
          </button>
        </div>
      </div>
      <p className={`mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-700 sm:text-base ${open ? "" : "line-clamp-3"}`} data-testid={`enquiry-message-${e.id}`}>{e.message}</p>
      {e.message.length > 220 && (
        <button onClick={() => setOpen(!open)} className="mt-2 text-sm font-medium text-sky-700 hover:text-sky-900" data-testid={`enquiry-toggle-${e.id}`}>
          {open ? "Show less" : "Read full message"}
        </button>
      )}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <StatusPicker e={e} onStatus={onStatus} />
      </div>
    </article>
  );
};
